import { useCallback, useEffect, useMemo, type Dispatch, type MutableRefObject, type SetStateAction } from 'react'
import { deleteDoc, doc } from 'firebase/firestore'
import { firestoreDb } from '../../services/firebase'
import { WORKSPACE_DEFAULT_CHECKLIST_ID, type TimelineElement } from '../../context/TimelineContext'
import type { useRecurringLens } from '../../context/recurringLensContextCore'
import type { useWorkspace } from '../../context/useWorkspace'
import type { useAuth } from '../../context/useAuth'
import type { ChecklistItem as BoardChecklistItem } from '../../types/schema'
import { CHECKLIST_CANVAS_LAYOUT_VERSION as WORKSPACE_CANVAS_LAYOUT_VERSION } from '../../types/checklistCanvas'
import { findEmptyCanvasSlot, toSlotBox, type CanvasSlotBox } from '../../services/canvasPlacementService'
import {
  GATE_DIAMETER,
  GRID_ROW_GAP,
  MAX_CHECKLIST_GRID_COLUMNS,
  NODE_GAP,
  NODE_HEIGHT,
  NODE_WIDTH,
} from '../../services/canvasLayoutConstants'
import {
  isGateNode,
  MIN_VIEWPORT_WIDTH,
  legacyDashboardBoardRootId,
} from './boardItem'
import {
  overlayLayoutPositionsForWorkspace,
  reconcileLiveChecklistBoardNodes,
} from './liveReconciliation'

export function useLiveReconciliation({
  item,
  recurringLens,
  elements,
  isBoardDirty,
  hasBoardSnapshotRef,
  timelineChecklistItems,
  nodesRef,
  deletedSourceIdsRef,
  activeWorkspace,
  currentUser,
  isBoardDirtyRef,
  setNodes,
  optimisticPositionsRef,
  boardDocumentIdsRef,
  baselineNodesRef,
  isCinematicModeActive,
  setIsCinematicModeActive,
  canEditCanvas,
  setIsGestureKnifeActive,
  weightCommitTimersRef,
  scrollFrameRef,
  tooltipTouchTimerRef,
  cursorWriteTimerRef,
  boardRef,
  setViewportSize,
}: {
  item: TimelineElement
  recurringLens: ReturnType<typeof useRecurringLens>
  elements: TimelineElement[]
  isBoardDirty: boolean
  hasBoardSnapshotRef: MutableRefObject<boolean>
  timelineChecklistItems: BoardChecklistItem[]
  nodesRef: MutableRefObject<BoardChecklistItem[]>
  deletedSourceIdsRef: MutableRefObject<Set<string>>
  activeWorkspace: ReturnType<typeof useWorkspace>['workspaces'][number] | undefined
  currentUser: ReturnType<typeof useAuth>['currentUser']
  isBoardDirtyRef: MutableRefObject<boolean>
  setNodes: Dispatch<SetStateAction<BoardChecklistItem[]>>
  optimisticPositionsRef: MutableRefObject<Record<string, { x: number; y: number }>>
  boardDocumentIdsRef: MutableRefObject<Set<string>>
  baselineNodesRef: MutableRefObject<BoardChecklistItem[]>
  isCinematicModeActive: boolean
  setIsCinematicModeActive: (value: boolean) => void
  canEditCanvas: boolean
  setIsGestureKnifeActive: (value: boolean) => void
  weightCommitTimersRef: MutableRefObject<Record<string, number>>
  scrollFrameRef: MutableRefObject<number | null>
  tooltipTouchTimerRef: MutableRefObject<number | null>
  cursorWriteTimerRef: MutableRefObject<number | null>
  boardRef: MutableRefObject<HTMLDivElement | null>
  setViewportSize: Dispatch<SetStateAction<{ width: number; height: number; scrollLeft: number; scrollTop: number }>>
}) {
  const isWorkspaceDefaultChecklist = item.id === WORKSPACE_DEFAULT_CHECKLIST_ID
  // Recurring lens mounts this board with a SYNTHETIC parent id
  // (`recurring:{ws}:{DAILY|WEEKLY}`) that does NOT correspond to any real
  // element document. Any element-mirror write / progress-rollup rooted at
  // `item.id` therefore has to be skipped in that mode too, not just for the
  // workspace-default aggregate. Consolidate both checks so every mirror
  // guard downstream is a single predicate.
  const hasElementMirror = !isWorkspaceDefaultChecklist && !recurringLens
  const dashboardElements = useMemo(
    () => elements.filter((element) => element.workspaceId === item.workspaceId && element.board === 'DASHBOARD'),
    [elements, item.workspaceId],
  )
  const dashboardContainerIds = useMemo(
    () => new Set([WORKSPACE_DEFAULT_CHECKLIST_ID, ...dashboardElements.map((element) => element.id)]),
    [dashboardElements],
  )
  const dashboardChecklistRootIds = useMemo(
    () => new Set([WORKSPACE_DEFAULT_CHECKLIST_ID, legacyDashboardBoardRootId(item.workspaceId)]),
    [item.workspaceId],
  )
  const isDashboardChecklistContainer = useCallback(
    // `item.id` handles synthetic parents (e.g. the Daily / Weekly recurring
    // canvas shells which mount ChecklistDrawingBoard with a synthetic
    // TimelineElement whose id is `recurring:${workspaceId}:${mode}`) — nodes
    // rooted under that synthetic id are exactly the items this canvas
    // should show.
    (containerId: string) =>
      containerId === item.id
      || dashboardContainerIds.has(containerId)
      || dashboardChecklistRootIds.has(containerId),
    [dashboardChecklistRootIds, dashboardContainerIds, item.id],
  )

  // Live-sync & dynamic append: `loadBoardFromDb` is a one-shot fetch by design.
  // We merge live TimelineContext rows into `nodes`:
  // 1. Overlay definition-owned display fields onto existing nodes.
  // 2. Append newly-created TimelineContext rows whose id is not yet in `nodes`,
  //    filtered by dashboard/container gates, overlaying their positions from
  //    the layout version (or falling back to findEmptyCanvasSlot), and updating
  //    baselineNodesRef and boardDocumentIdsRef.
  // Skip while the board is dirty so in-flight edits are never clobbered.
  useEffect(() => {
    if (isBoardDirty) return
    if (!hasBoardSnapshotRef.current) return
    if (timelineChecklistItems.length === 0) return

    const currentNodes = nodesRef.current
    const {
      updatedExisting,
      hasOverlayChanges,
      normalizedNewNodes,
    } = reconcileLiveChecklistBoardNodes({
      currentNodes,
      timelineRows: timelineChecklistItems,
      containerItemId: item.id,
      workspaceId: item.workspaceId,
      deletedSourceIds: deletedSourceIdsRef.current,
      isDashboardContainer: isDashboardChecklistContainer,
      recurringLens,
      activeWorkspaceOwnerId: activeWorkspace?.ownerId,
      currentUserId: currentUser?.uid,
    })

    if (hasOverlayChanges) {
      setNodes((current) => (hasOverlayChanges ? updatedExisting : current))
    }

    if (normalizedNewNodes.length === 0) return

    let cancelled = false

    void overlayLayoutPositionsForWorkspace(item.workspaceId, recurringLens, normalizedNewNodes).then((overlaidNewNodes) => {
      if (cancelled) return
      if (isBoardDirtyRef.current) return

      setNodes((latestCurrent) => {
        if (isBoardDirtyRef.current) return latestCurrent
        const latestIds = new Set(latestCurrent.map((n) => n.id))
        const unadded = overlaidNewNodes.filter((n) => !latestIds.has(n.id))
        if (unadded.length === 0) return latestCurrent

        const placedUnadded: BoardChecklistItem[] = []
        for (const candidate of unadded) {
          const hasNoPosition = !candidate.position || (candidate.position.x === 0 && candidate.position.y === 0)
          if (hasNoPosition) {
            const isGate = isGateNode(candidate)
            const width = isGate ? GATE_DIAMETER : NODE_WIDTH
            const height = isGate ? GATE_DIAMETER : NODE_HEIGHT

            const existingBoxes: CanvasSlotBox[] = [
              ...latestCurrent
                .filter((node) => node.id !== candidate.id)
                .map((node) =>
                  toSlotBox({
                    position: optimisticPositionsRef.current[node.id] ?? node.position,
                    nodeKind: node.nodeKind,
                  }),
                ),
              ...placedUnadded.map((node) =>
                toSlotBox({
                  position: node.position,
                  nodeKind: node.nodeKind,
                }),
              ),
            ]

            const freshPosition = findEmptyCanvasSlot({
              existing: existingBoxes,
              newNode: { width, height },
            })

            placedUnadded.push({
              ...candidate,
              position: freshPosition,
            })
          } else {
            placedUnadded.push(candidate)
          }
        }

        placedUnadded.forEach((n) => boardDocumentIdsRef.current.add(n.id))
        baselineNodesRef.current = [...baselineNodesRef.current, ...placedUnadded]
        return [...latestCurrent, ...placedUnadded]
      })
    })

    return () => {
      cancelled = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: baselineNodesRef, boardDocumentIdsRef, deletedSourceIdsRef, hasBoardSnapshotRef, isBoardDirtyRef, nodesRef, optimisticPositionsRef, setNodes are useRef/useState identities passed from the Surface (stable)
  }, [activeWorkspace?.ownerId, currentUser?.uid, isBoardDirty, isDashboardChecklistContainer, item.id, item.workspaceId, recurringLens, timelineChecklistItems])

  const arrangeWorkspaceNodes = useCallback((items: BoardChecklistItem[]) => {
    const elementById = new Map(dashboardElements.map((element) => [element.id, element]))
    const originalOrder = new Map(items.map((node, index) => [node.id, index]))
    const hierarchyPathCache = new Map<string, string>()
    const hierarchyPath = (containerId: string) => {
      const cached = hierarchyPathCache.get(containerId)
      if (cached) return cached
      if (dashboardChecklistRootIds.has(containerId)) return '0-dashboard'
      const chain: TimelineElement[] = []
      let current = elementById.get(containerId)
      while (current) {
        chain.unshift(current)
        current = current.parentId ? elementById.get(current.parentId) : undefined
      }
      const path = `1-${chain.map((element) => element.routePath ?? element.title).join('/')}`
      hierarchyPathCache.set(containerId, path)
      return path
    }
    dashboardChecklistRootIds.forEach((containerId) => hierarchyPathCache.set(containerId, '0-dashboard'))
    const checklistNodes = items
      .filter((node) => !isGateNode(node))
      .sort((first, second) => {
        const pathOrder = hierarchyPath(first.parentContainerId).localeCompare(hierarchyPath(second.parentContainerId))
        return pathOrder || (originalOrder.get(first.id) ?? 0) - (originalOrder.get(second.id) ?? 0)
      })
    const columns = Math.min(MAX_CHECKLIST_GRID_COLUMNS, Math.max(1, Math.ceil(Math.sqrt(checklistNodes.length))))
    const arranged = new Map(checklistNodes.map((node, index) => [node.id, {
      x: (index % columns) * (NODE_WIDTH + NODE_GAP),
      y: Math.floor(index / columns) * (NODE_HEIGHT + GRID_ROW_GAP),
    }]))

    return items.map((node) => ({
      ...node,
      position: arranged.get(node.id) ?? node.position,
      workspaceCanvasLayoutVersion: WORKSPACE_CANVAS_LAYOUT_VERSION,
    }))
  }, [dashboardChecklistRootIds, dashboardElements])

  useEffect(() => {
    if (!isCinematicModeActive) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setIsCinematicModeActive(false)
      }
    }

    document.documentElement.classList.add('checklistCinematicModeActive')
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.documentElement.classList.remove('checklistCinematicModeActive')
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isCinematicModeActive, setIsCinematicModeActive])

  useEffect(() => {
    if (!canEditCanvas) return

    let touchHoldTimer: number | null = null
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Shift') setIsGestureKnifeActive(true)
    }
    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Shift') setIsGestureKnifeActive(false)
    }
    const clearTouchHold = () => {
      if (touchHoldTimer !== null) {
        window.clearTimeout(touchHoldTimer)
        touchHoldTimer = null
      }
      setIsGestureKnifeActive(false)
    }
    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length < 2) return
      if (touchHoldTimer !== null) window.clearTimeout(touchHoldTimer)
      touchHoldTimer = window.setTimeout(() => {
        setIsGestureKnifeActive(true)
      }, 420)
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    window.addEventListener('touchstart', handleTouchStart, { passive: true })
    window.addEventListener('touchend', clearTouchHold)
    window.addEventListener('touchcancel', clearTouchHold)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchend', clearTouchHold)
      window.removeEventListener('touchcancel', clearTouchHold)
      clearTouchHold()
    }
  }, [canEditCanvas, setIsGestureKnifeActive])

  useEffect(() => () => {
    Object.values(weightCommitTimersRef.current).forEach((timerId) => window.clearTimeout(timerId))
    weightCommitTimersRef.current = {}
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: weightCommitTimersRef are useRef/useState identities passed from the Surface (stable)
  }, [])

  useEffect(() => () => {
    if (scrollFrameRef.current !== null) window.cancelAnimationFrame(scrollFrameRef.current)
    if (tooltipTouchTimerRef.current !== null) window.clearTimeout(tooltipTouchTimerRef.current)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: scrollFrameRef, tooltipTouchTimerRef are useRef/useState identities passed from the Surface (stable)
  }, [])

  useEffect(() => () => {
    if (cursorWriteTimerRef.current !== null) window.clearTimeout(cursorWriteTimerRef.current)
    if (currentUser && item.workspaceId) {
      void deleteDoc(doc(firestoreDb, 'workspaces', item.workspaceId, 'cursors', currentUser.uid)).catch(() => undefined)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: cursorWriteTimerRef are useRef/useState identities passed from the Surface (stable)
  }, [currentUser, item.workspaceId])

  useEffect(() => {
    const board = boardRef.current
    if (!board) return

    const syncWidth = () => {
      setViewportSize((current) => ({
        ...current,
        width: Math.max(MIN_VIEWPORT_WIDTH, Math.round(board.getBoundingClientRect().width)),
        scrollLeft: board.scrollLeft,
        scrollTop: board.scrollTop,
      }))
    }
    syncWidth()

    const observer = new ResizeObserver(syncWidth)
    observer.observe(board)
    return () => observer.disconnect()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: boardRef, setViewportSize are useRef/useState identities passed from the Surface (stable)
  }, [])

  return {
    isWorkspaceDefaultChecklist,
    hasElementMirror,
    dashboardElements,
    dashboardContainerIds,
    dashboardChecklistRootIds,
    isDashboardChecklistContainer,
    arrangeWorkspaceNodes,
  }
}
